import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    ArrowCounterClockwiseIcon,
    LockOpenIcon,
    PencilSimpleIcon,
    TrashIcon,
} from '@phosphor-icons/react';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { formatRelativeTimeI18n } from '@/utils/date';
import { Table } from '@/components/Table';
import {
    ActionBar,
    ActionType,
    ActionVariant,
    type ActionConfig,
} from '@/components/ActionBar';
import { AppRoutes } from '@/constants/routes';
import { MemberState } from '@/types/admin-settings';
import type { AuditEvent, Member } from '@/types/admin-settings';
import { UserAvatar } from '@/components/NavigationBar/UserAvatar';
import { useAuthUser } from '@/hooks/useAuthUser';
import { useAsyncAction } from '@/hooks/useAsyncAction';
import { Permission } from '@/hooks/usePermissions';
import { AdminSettingsService } from '@/services/engine/admin-settings-service';
import { useMemberDetail } from '../hooks/useMemberDetail';
import { useAuditEventColumns } from '../hooks/useAuditEventColumns';
import { EditRolesModal } from './EditRolesModal';
import { MemberDetailSkeleton } from './MemberDetailSkeleton';
import { RemoveMemberModal } from './RemoveMemberModal';
import { ResetPasswordModal } from './ResetPasswordModal';
import { RoleBadge } from './RoleBadge';
import { Chip } from '@/components/Chip';
import type { ChipVariant } from '@/components/Chip';

const STATE_LABELS: Record<MemberState, string> = {
    [MemberState.ACTIVE]: 'Active',
    [MemberState.INACTIVE]: 'Inactive',
    [MemberState.LOCKED]: 'Locked',
    [MemberState.INITIAL]: 'Initial',
    [MemberState.UNSPECIFIED]: 'Unknown',
    [MemberState.DELETED]: 'Deleted',
};

const EVENTS_PAGE_SIZE = 10;

const STATE_VARIANTS: Record<MemberState, ChipVariant> = {
    [MemberState.ACTIVE]: 'positive',
    [MemberState.INACTIVE]: 'default',
    [MemberState.LOCKED]: 'negative',
    [MemberState.INITIAL]: 'default',
    [MemberState.UNSPECIFIED]: 'default',
    [MemberState.DELETED]: 'negative',
};

export function MemberDetailView() {
    const { userId } = useParams<{ userId: string }>();
    const { t } = useTranslation(I18nNamespace.SETTINGS);
    const navigate = useNavigate();
    const { user: currentUser } = useAuthUser();
    const { member, error, mutate } = useMemberDetail(userId ?? '');
    const [editingMember, setEditingMember] = useState<Member | null>(null);
    const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
    const [removeOpen, setRemoveOpen] = useState(false);
    const [eventsPage, setEventsPage] = useState(1);

    const columns = useAuditEventColumns();

    const { isSubmitting: isUnlocking, run: unlock } = useAsyncAction(
        async () => {
            if (!member) return;
            await AdminSettingsService.unlockMember(member.id);
            await mutate();
        },
        {
            successMessage: t('members.unlockSuccess', {
                name: member?.name ?? '',
            }),
            errorMessage: t('members.unlockFailed'),
        },
    );

    if (error) {
        return (
            <div className="flex items-center justify-center py-20 text-danger-400">
                {t('members.loadFailed')}
            </div>
        );
    }

    const isSelf = !!member && currentUser?.id === member.id;
    const isLocked = member?.state === MemberState.LOCKED;
    const actions: ActionConfig[] = [
        ...(isLocked
            ? [
                  {
                      type: ActionType.ITEM as const,
                      icon: LockOpenIcon,
                      label: t('members.unlock'),
                      permission: Permission.ORGANIZATION_SETTINGS_EDIT,
                      disabled: isUnlocking,
                      onClick: unlock,
                  },
              ]
            : []),
        {
            type: ActionType.ITEM,
            icon: PencilSimpleIcon,
            label: t('members.editRoles'),
            disabled: !member,
            onClick: () => {
                if (!member) return;
                setEditingMember({
                    id: member.id,
                    grantId: member.grantId,
                    email: member.email,
                    name: member.name,
                    roles: member.roles,
                    lastSeen: member.lastLogin,
                    isOrgOwner: member.isOrgOwner,
                    isOrgUserManager: member.isOrgUserManager,
                });
            },
        },
        {
            type: ActionType.ITEM,
            icon: ArrowCounterClockwiseIcon,
            label: t('members.resetPassword'),
            disabled: !member,
            onClick: () => setResetPasswordOpen(true),
        },
        ...(!isSelf
            ? [
                  {
                      type: ActionType.ITEM as const,
                      icon: TrashIcon,
                      label: t('members.remove'),
                      variant: ActionVariant.DANGER,
                      disabled: !member,
                      onClick: () => setRemoveOpen(true),
                  },
              ]
            : []),
    ];

    return (
        <div className="flex flex-col gap-8 py-6">
            <div className="flex justify-center">
                <ActionBar actions={actions} />
            </div>

            {!member ? (
                <MemberDetailSkeleton />
            ) : (
                <>
                    <div className="flex items-center gap-4">
                        <UserAvatar name={member.name} size="md" />
                        <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2">
                                <h1 className="text-2xl font-semibold text-grey-100">
                                    {member.name}
                                </h1>
                                {member.isOrgOwner && (
                                    <RoleBadge
                                        key="owner"
                                        role={t('members.owner')}
                                        variant="positive"
                                    />
                                )}
                                {member.isOrgUserManager && (
                                    <RoleBadge
                                        key="user-manager"
                                        role={t('members.userManager')}
                                        variant="positive"
                                    />
                                )}
                            </div>
                            <p className="text-sm text-grey-300">
                                {member.email}
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 @wide:grid-cols-4 gap-6">
                        <div className="flex flex-col gap-1">
                            <span className="text-xs text-grey-400 uppercase tracking-wide">
                                {t('members.role')}
                            </span>
                            <div className="flex items-center gap-1.5 flex-wrap">
                                {member.roles.length > 0 ? (
                                    member.roles.map((role) => (
                                        <RoleBadge key={role} role={role} />
                                    ))
                                ) : (
                                    <span className="text-sm text-grey-300">
                                        -
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="flex flex-col gap-1">
                            <span className="text-xs text-grey-400 uppercase tracking-wide">
                                {t('members.state')}
                            </span>
                            <div className="flex items-center gap-1.5 flex-wrap">
                                <Chip
                                    value={
                                        STATE_LABELS[member.state] ??
                                        member.state
                                    }
                                    variant={
                                        STATE_VARIANTS[member.state] ??
                                        'default'
                                    }
                                />
                            </div>
                        </div>
                        <div className="flex flex-col gap-1">
                            <span className="text-xs text-grey-400 uppercase tracking-wide">
                                {t('members.lastLogin')}
                            </span>
                            <span className="text-sm text-grey-100">
                                {member.lastLogin
                                    ? formatRelativeTimeI18n(
                                          new Date(member.lastLogin),
                                      )
                                    : '-'}
                            </span>
                        </div>
                        <div className="flex flex-col gap-1">
                            <span className="text-xs text-grey-400 uppercase tracking-wide">
                                {t('members.invitedBy')}
                            </span>
                            <span className="text-sm text-grey-100">
                                {member.invitedByEmail ?? '-'}
                            </span>
                        </div>
                    </div>

                    <div className="flex flex-col gap-3">
                        <h2 className="text-lg font-medium text-grey-100">
                            {t('members.recentActivity')}
                        </h2>
                        <Table<AuditEvent>
                            data={member.recentEvents.slice(
                                (eventsPage - 1) * EVENTS_PAGE_SIZE,
                                eventsPage * EVENTS_PAGE_SIZE,
                            )}
                            columns={columns}
                            page={eventsPage}
                            pageSize={EVENTS_PAGE_SIZE}
                            totalCount={member.recentEvents.length}
                            onPageChange={setEventsPage}
                            emptyState={{
                                title: t('members.noRecentActivity'),
                            }}
                        />
                    </div>
                </>
            )}

            {editingMember && (
                <EditRolesModal
                    member={editingMember}
                    onClose={() => setEditingMember(null)}
                    onSaved={() => {
                        setEditingMember(null);
                        mutate();
                    }}
                />
            )}

            {resetPasswordOpen && member && (
                <ResetPasswordModal
                    userId={member.id}
                    userName={member.name}
                    onClose={() => setResetPasswordOpen(false)}
                />
            )}

            {removeOpen && member && (
                <RemoveMemberModal
                    userId={member.id}
                    userEmail={member.email}
                    userName={member.name}
                    onClose={() => setRemoveOpen(false)}
                    onRemoved={() => {
                        setRemoveOpen(false);
                        navigate(AppRoutes.SETTINGS);
                    }}
                />
            )}
        </div>
    );
}
